import React, { useState } from 'react';
import useTooltipPosition from '../../../hooks/useTooltipPosition';
import { getTierColor } from '../../../services/TierColorService';

const ClientList = ({ country, clientData, isPinned, togglePinClient, setHoveredClient, pinHeaderState, handleTogglePinAllCountry }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { calculateTooltipPosition } = useTooltipPosition();
  
  if (!country) return null;
  
  // Filter and sort clients by group size in ascending order
  const countryClients = clientData
    .filter(client => client.country === country.country)
    .sort((a, b) => {
      const sizeA = parseInt(a.group_size) || 0;
      const sizeB = parseInt(b.group_size) || 0;
      return sizeA - sizeB; // Ascending order (smallest first)
    });
  
  // Simple pagination
  const clientsPerPage = 5;
  const totalPages = Math.ceil(countryClients.length / clientsPerPage);
  const safeCurrentPage = Math.min(currentPage, totalPages || 1);
  
  const startIndex = (safeCurrentPage - 1) * clientsPerPage;
  const endIndex = startIndex + clientsPerPage;
  const currentClients = countryClients.slice(startIndex, endIndex);
  
  // Simple handler functions
  const handlePrevPage = () => {
    if (safeCurrentPage > 1) {
      setCurrentPage(safeCurrentPage - 1);
      setHoveredClient(null);
    }
  };
  
  const handleNextPage = () => {
    if (safeCurrentPage < totalPages) {
      setCurrentPage(safeCurrentPage + 1);
      setHoveredClient(null);
    }
  };
  
  // Get client details with enhanced handling for new data structure
  const getClientDetails = (client) => {
    return {
      // Financial information - now with proper formatting
      revenue: client['Total Revenue'] || '$0.00',
      lastBookingRevenue: client['Last Booking Revenue'] || 'N/A',
      lastBooked: client['Last Booked'] || 'N/A',
      
      // Contact information - with improved defaults
      contactName: client['Key Contact Name'] || 'Contact Representative',
      contactEmail: client['Key Contact Email'] || 'contact@client.com',
      previousManager: client['Previously Assigned Manager'] || 'None',
      
      // New fields available from portfolio data
      timeZone: client['Time zone'] || 'N/A',
      tier: client['Tier'] || 'N/A',
      segment: client['Segment'] || 'N/A',
      rn: client['RN'] || 'N/A'
    };
  };

  // Handle client pinning
  const handleClientPin = (client, e) => {
    e.preventDefault(); // Prevent the hover effect from being triggered
    togglePinClient(client);
  };

  return (
    <div className="client-section">
      <h3>Client Groups</h3>
      <div className="client-list">
        <div className="client-row header">
          <span>Client</span>
          <span>Size</span>
          <span>Manager</span>
          <span className="pin-header-label">
            Tier
          </span>
        </div>
        {currentClients.map((client, index) => {
          // Get tier color for this client
          const clientTier = client['Tier'] || 'Unknown';
          const tierColor = getTierColor(clientTier, 'dark');
          
          return (
            <div 
              key={index} 
              className={`client-row ${isPinned(client) ? 'pinned' : ''}`}
            >
              <span className="client-name">{client.client_name}</span>
              <span className="client-size">{client.group_size}</span>
              <span className="client-manager">{client.conference_manager}</span>
              <span 
                className="pin-icon disabled" 
                onMouseEnter={(e) => {
                  const { x, y } = calculateTooltipPosition(e.target);
                  
                  const tooltipData = {
                    ...client, 
                    details: getClientDetails(client), 
                    index,
                    x: x,
                    y: y
                  };
                  setHoveredClient(tooltipData);
                }}
                onMouseLeave={() => setHoveredClient(null)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tierColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{opacity: 0.7}}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </span>
            </div>
          );
        })}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="page-btn" 
            disabled={safeCurrentPage === 1}
            onClick={handlePrevPage}
          >
            &laquo;
          </button>
          <span className="page-info">Page {safeCurrentPage} of {totalPages}</span>
          <button 
            className="page-btn" 
            disabled={safeCurrentPage === totalPages}
            onClick={handleNextPage}
          >
            &raquo;
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientList;
